import { translateBlock } from 'i18n';
import { capitalize } from 'lodash';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
    BudgetProposalT,
    BudgetVentilatedDataWithCadrageT,
} from 'routes/BudgetModule/config/types';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { IntegrationT } from 'routes/IntegrationsList/types';
import { HttpError } from 'store/APIClient';
import GenericAutocomplete from 'ui/DropdownItems/GenericAutocomplete';
import { AutocompletAble } from 'ui/DropdownItems/types';
import Flex from 'ui/Flex/Flex';
import BudgetChartSection from 'routes/BudgetModule/components/shared/BudgetChart/BudgetChartSection/BudgetChartSection';
import BudgetContent from 'routes/BudgetModule/components/shared/BudgetContent';
import BudgetVentilatedByResource from '../BudgetVentilated/BudgetVentilatedByResource';
import HttpErrorWrapper from './ExpenseIncome/components/HttpErrorWrapper';
import {
    getCustomFilterLabel,
    isCustomBudgetProposal,
} from './hooks/budgetProposal';
import BudgetReportToggle from '../../shared/BudgetReportToggle/BudgetReportToggle';

type InscriptionData =
    | {
          data: InscriptionT[];
          columns: ResponseColumnT[];
      }
    | undefined;

type Props = {
    budgetProposal: BudgetProposalT;
    code: AutocompletAble<string> | null;
    setCodes: React.Dispatch<
        React.SetStateAction<AutocompletAble<string> | null>
    >;
    codeOptions: AutocompletAble<string>[];
    isError: boolean;
    error: HttpError | Error | null;
    inscriptionsData: InscriptionData;
    budgetVentilatedData: BudgetVentilatedDataWithCadrageT[] | undefined;
    budgetVentilatedQuery: QueryT;
    integrations?: IntegrationT[];
};

export const BudgetProposalByResource: React.FC<Props> = ({
    code,
    setCodes,
    codeOptions,
    isError,
    error,
    budgetProposal,
    inscriptionsData,
    budgetVentilatedData,
    budgetVentilatedQuery,
    integrations,
}) => {
    const isSchool = isCustomBudgetProposal(integrations);
    const { budgetProposalId: budgetProposalIdParam } = useParams();
    const budgetProposalId = budgetProposalIdParam
        ? parseInt(budgetProposalIdParam, 10)
        : -1;

    return (
        <BudgetContent sx={!isSchool ? { pt: '1rem' } : undefined}>
            <HttpErrorWrapper error={error} isError={isError}>
                <Flex direction="row" justifyContent="end">
                    {isSchool ? (
                        <GenericAutocomplete
                            selectedValues={code}
                            availableValues={codeOptions}
                            setSelectedValues={setCodes}
                            label={capitalize(
                                translateBlock(
                                    getCustomFilterLabel(integrations),
                                ),
                            )}
                            sx={{ m: '0', width: '30%', mb: '1rem' }}
                        />
                    ) : (
                        <BudgetReportToggle
                            exercice={budgetProposal.exercice}
                            context={{
                                page: 'Dépenses/Recettes',
                                budgetProposal: budgetProposal.budgetName,
                                section: budgetProposal.section ?? '',
                            }}
                        />
                    )}
                </Flex>
                <BudgetChartSection
                    budgetProposal={budgetProposal}
                    ventilationType="resource"
                    ventilatedData={{
                        query: budgetVentilatedQuery,
                        data: budgetVentilatedData!,
                    }}
                />

                {/* Without this ID check it was sometimes loading the component with a deprecated budget proposal at first */}
                {/* if a bp in service proposal phase was loaded before a bp in arbitrage phase, */}
                {/* the query was launched with the wrong phase then the wrong columns were fetched */}
                {budgetProposal.id &&
                    budgetProposal.id === budgetProposalId && (
                        <BudgetVentilatedByResource
                            budgetProposal={budgetProposal}
                            inscriptionsData={inscriptionsData!}
                            ventilatedData={budgetVentilatedData!}
                        />
                    )}
            </HttpErrorWrapper>
        </BudgetContent>
    );
};
